import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useToast } from '@/hooks/use-toast';
import type { MentorProfile, MentorshipConnection, MentorMatch, MentorshipScope } from '@/types/mentorship';

interface AISkillMatch {
  mentorIndex: number;
  matchingSkills: {
    wantToLearn: string;
    canTeach: string;
    relevance: 'exact' | 'high' | 'medium';
  }[];
  overallScore: number;
}

export function useMentorship() {
  const { user } = useAuth();
  const { profile } = useProfile();
  const { toast } = useToast();
  const [myProfile, setMyProfile] = useState<MentorProfile | null>(null);
  const [allMentors, setAllMentors] = useState<MentorProfile[]>([]);
  const [connections, setConnections] = useState<MentorshipConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [aiMatches, setAiMatches] = useState<MentorMatch[]>([]);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Derive organization from cached profile
  const myOrganization = profile?.organization || null;

  // Fetch my mentor profile
  const fetchMyProfile = useCallback(async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('mentor_profiles')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) {
      console.error('Error fetching mentor profile:', error);
    } else {
      setMyProfile(data as MentorProfile | null);
    }
  }, [user]);

  // Fetch available mentors with profile data (no filtering here - done with useMemo)
  const fetchMentors = useCallback(async () => {
    if (!user) return;

    // First get mentor profiles
    const { data: mentorData, error: mentorError } = await supabase
      .from('mentor_profiles')
      .select('*')
      .eq('is_available_as_mentor', true)
      .neq('user_id', user.id);

    if (mentorError) {
      console.error('Error fetching mentors:', mentorError);
      return;
    }

    if (!mentorData || mentorData.length === 0) {
      setAllMentors([]);
      return;
    }

    // Get profile data for each mentor (only non-sensitive fields via profiles_public view)
    const userIds = mentorData.map(m => m.user_id);
    const { data: profileData, error: profileError } = await supabase
      .from('profiles_public')
      .select('user_id, display_name, avatar_url, job_role, first_name, last_name, organization, founding_member')
      .in('user_id', userIds);

    if (profileError) {
      console.error('Error fetching profiles:', profileError);
    }

    // Merge profile data
    const profileMap = new Map(profileData?.map(p => [p.user_id, p]) || []);
    const enrichedMentors = mentorData.map(mentor => ({
      ...mentor,
      ...profileMap.get(mentor.user_id),
    })) as MentorProfile[];

    setAllMentors(enrichedMentors);
  }, [user]);

  // Filter mentors by organization scope (memoized to prevent re-render loops)
  const mentors = useMemo(() => {
    return allMentors.filter(mentor => {
      const mentorOrg = mentor.organization;
      const mentorScope = (mentor.mentorship_scope || 'both') as MentorshipScope;
      const myScope = (myProfile?.mentorship_scope || 'both') as MentorshipScope;

      // If either has no organization set, only match if they're open to 'both'
      if (!mentorOrg || !myOrganization) {
        return mentorScope === 'both' && myScope === 'both';
      }

      const sameOrg = mentorOrg.toLowerCase() === myOrganization.toLowerCase();

      // Check mentor's preference
      if (mentorScope === 'internal' && !sameOrg) return false;
      if (mentorScope === 'external' && sameOrg) return false;

      // Check my preference
      if (myScope === 'internal' && !sameOrg) return false;
      if (myScope === 'external' && sameOrg) return false;

      return true;
    });
  }, [allMentors, myProfile?.mentorship_scope, myOrganization]);

  // Fetch my connections
  const fetchConnections = useCallback(async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('mentorship_connections')
      .select('*')
      .or(`mentor_id.eq.${user.id},mentee_id.eq.${user.id}`)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching connections:', error);
    } else {
      setConnections((data || []) as MentorshipConnection[]);
    }
  }, [user]);

  // Initial fetch - only runs once per user
  useEffect(() => {
    if (!user || initialLoadDone) return;

    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchMyProfile(), fetchMentors(), fetchConnections()]);
      setLoading(false);
      setInitialLoadDone(true);
    };

    loadData();
  }, [user, initialLoadDone, fetchMyProfile, fetchMentors, fetchConnections]);

  // Reset initial load when user changes
  useEffect(() => {
    if (!user) {
      setInitialLoadDone(false);
      setLoading(true);
    }
  }, [user]);

  // Create or update my mentor profile
  const saveMyProfile = async (profile: Partial<MentorProfile>) => {
    if (!user) return;

    const profileData = {
      user_id: user.id,
      is_available_as_mentor: profile.is_available_as_mentor ?? false,
      is_seeking_mentor: profile.is_seeking_mentor ?? false,
      bio: profile.bio || null,
      skills_can_teach: profile.skills_can_teach || [],
      skills_want_to_learn: profile.skills_want_to_learn || [],
      years_experience: profile.years_experience || null,
      max_mentees: profile.max_mentees || 3,
      preferred_meeting_frequency: profile.preferred_meeting_frequency || 'weekly',
      mentorship_scope: profile.mentorship_scope || 'both',
    };

    const { error } = await supabase
      .from('mentor_profiles')
      .upsert(profileData, { onConflict: 'user_id' });

    if (error) {
      toast({
        title: 'Error saving profile',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    }

    await fetchMyProfile();
    toast({
      title: 'Mentor profile saved',
      description: 'Your mentorship settings have been updated.',
    });
    return true;
  };

  // Request mentorship connection
  const requestMentorship = async (mentorId: string, skillFocus: string, message: string) => {
    if (!user) return;

    const { error } = await supabase
      .from('mentorship_connections')
      .insert({
        mentor_id: mentorId,
        mentee_id: user.id,
        skill_focus: skillFocus,
        message: message,
        status: 'pending',
      });

    if (error) {
      if (error.code === '23505') {
        toast({
          title: 'Request already sent',
          description: 'You already have a pending request for this skill.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Error sending request',
          description: error.message,
          variant: 'destructive',
        });
      }
      return false;
    }

    await fetchConnections();
    toast({
      title: 'Request sent!',
      description: 'Your mentorship request has been sent.',
    });
    return true;
  };

  // Respond to a connection request (accept/decline) - only mentors can do this
  const respondToRequest = async (connectionId: string, accept: boolean) => {
    if (!user) return false;

    // Client-side validation: verify the current user is the mentor for this connection
    const connection = connections.find(c => c.id === connectionId);
    if (!connection) {
      toast({
        title: 'Error',
        description: 'Connection not found.',
        variant: 'destructive',
      });
      return false;
    }

    if (connection.mentor_id !== user.id) {
      toast({
        title: 'Unauthorized',
        description: 'Only the mentor can respond to connection requests.',
        variant: 'destructive',
      });
      return false;
    }

    const { error } = await supabase
      .from('mentorship_connections')
      .update({ status: accept ? 'accepted' : 'declined' })
      .eq('id', connectionId);

    if (error) {
      toast({
        title: 'Error responding to request',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    }

    await fetchConnections();
    toast({
      title: accept ? 'Connection accepted!' : 'Request declined',
      description: accept ? 'You can now message your mentee.' : 'The request has been declined.',
    });
    return true;
  };

  // End a mentorship connection
  const endConnection = async (connectionId: string) => {
    const { error } = await supabase
      .from('mentorship_connections')
      .update({ status: 'ended' })
      .eq('id', connectionId);

    if (error) {
      toast({
        title: 'Error ending connection',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    }

    await fetchConnections();
    return true;
  };

  // Basic fallback matching (exact string match)
  const getBasicMatches = useCallback((): MentorMatch[] => {
    if (!myProfile || !myProfile.skills_want_to_learn.length) return [];

    return mentors
      .map(mentor => {
        const matchingSkills = mentor.skills_can_teach.filter(skill =>
          myProfile.skills_want_to_learn.some(
            wantSkill => wantSkill.toLowerCase() === skill.toLowerCase()
          )
        );
        return {
          profile: mentor,
          matchScore: matchingSkills.length * 25, // Scale to 0-100
          matchingSkills,
        };
      })
      .filter(match => match.matchScore > 0)
      .sort((a, b) => b.matchScore - a.matchScore);
  }, [mentors, myProfile]);

  // Fetch AI-powered skill matches
  const fetchAIMatches = useCallback(async () => {
    if (!myProfile || !myProfile.skills_want_to_learn.length || !mentors.length) {
      setAiMatches([]);
      return;
    }

    setMatchesLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('match-skills', {
        body: {
          skillsWantToLearn: myProfile.skills_want_to_learn,
          mentors: mentors.map(m => ({
            user_id: m.user_id,
            skills_can_teach: m.skills_can_teach,
          })),
        },
      });

      if (error) {
        console.error('Error fetching AI matches:', error);
        const isRateLimited = data?.retry_after_seconds || error?.message?.includes('Too many requests');
        toast({
          title: isRateLimited ? 'Too many requests' : 'Matching error',
          description: isRateLimited
            ? 'AI matching is temporarily limited. Using basic matching.'
            : 'Could not fetch AI matches. Using basic matching.',
          variant: 'destructive',
        });
        // Fall back to basic matching
        setAiMatches(getBasicMatches());
        return;
      }

      if (data?.error) {
        console.error('AI matching error:', data.error);
        setAiMatches(getBasicMatches());
        return;
      }

      const aiMatchData = data?.matches as AISkillMatch[] || [];
      
      // Convert AI matches to MentorMatch format
      const enrichedMatches: MentorMatch[] = aiMatchData
        .filter(match => match.overallScore > 0)
        .map(match => {
          const mentor = mentors[match.mentorIndex];
          if (!mentor) return null;
          
          return {
            profile: mentor,
            matchScore: match.overallScore,
            matchingSkills: match.matchingSkills.map(s => 
              `${s.wantToLearn} ↔ ${s.canTeach}${s.relevance !== 'exact' ? ` (${s.relevance})` : ''}`
            ),
          };
        })
        .filter((m): m is MentorMatch => m !== null)
        .sort((a, b) => b.matchScore - a.matchScore);

      setAiMatches(enrichedMatches);
    } catch (err) {
      console.error('Failed to fetch AI matches:', err);
      setAiMatches(getBasicMatches());
    } finally {
      setMatchesLoading(false);
    }
  }, [myProfile, mentors, toast, getBasicMatches]);

  // Trigger AI matching when profile or mentors change (only after initial load)
  useEffect(() => {
    if (initialLoadDone && myProfile && mentors.length > 0) {
      fetchAIMatches();
    }
  }, [initialLoadDone, myProfile, mentors, fetchAIMatches]);

  const refetch = useCallback(async () => {
    await Promise.all([fetchMyProfile(), fetchMentors(), fetchConnections()]);
  }, [fetchMyProfile, fetchMentors, fetchConnections]);

  return {
    myProfile,
    myOrganization,
    mentors,
    connections,
    loading,
    matchesLoading,
    aiMatches,
    saveMyProfile,
    requestMentorship,
    respondToRequest,
    endConnection,
    getMatches: () => aiMatches.length > 0 ? aiMatches : getBasicMatches(),
    refetch,
  };
}
